<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import Layout from '@layouts/main.vue'
import { mapActions, mapGetters } from 'vuex'
import { getWeekRangeStringFromTimesheet } from '@/helpers/timesheet-helpers'
import TimesheetChangeTimeCard from '@/components/timesheet/timesheet-change-time-card.vue'
import { TimesheetPrimaryGroupingType } from '@/shared/constants/timesheet/TimesheetPrimaryGroupingType'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import TimesheetSubmitResponseViewModel from '@/models/timesheets/timesheetSubmitResponseViewModel'
import TimesheetSubmitIssuesAlert from '@/components/timesheet/timesheet-submit-issues-alert.vue'
import TimesheetExpPanelsSkeletonLoader from '@/components/skeleton/timesheet-exp-panels-skeleton-loader.vue'
import TimesheetSelectionActionBar from '@/components/timesheet/timesheet-selection-action-bar.vue'
import TimesheetPrimaryGroupingToggle from '@/components/timesheet/timesheet-primary-grouping-toggle.vue'
import TimesheetPrimaryGroupPanel from '@/components/timesheet/timesheet-primary-group-panel.vue'
// eslint-disable-next-line no-unused-vars
import TimesheetsPendingApprovalViewModel from '@/models/timesheets/timesheetsPendingApprovalViewModel'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import TimesheetsPrimaryGroupDisplayableItem from '@/models/timesheets/timesheetsPrimaryGroupDisplayableItem'
import TimesheetsPendingApprovalMainListItem from '@/models/timesheets/timesheetsPendingApprovalMainListItem'
import TimesheetsWeekGroupingListItem from '@/models/timesheets/timesheetsWeekGroupingListItem'

export default {
  name: 'TimesheetsView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Timesheets page.' }],
    }
  },
  components: {
    Layout,
    TimesheetChangeTimeCard,
    TimesheetSubmitIssuesAlert,
    TimesheetExpPanelsSkeletonLoader,
    TimesheetSelectionActionBar,
    EmptyStateBlock,
    TimesheetPrimaryGroupingToggle,
    TimesheetPrimaryGroupPanel,
  },
  mixins: [baseMixin, timesheetMixin],
  data: () => {
    return {
      /**
       * @type {Array<TimesheetsPendingApprovalMainListItem>}
       */
      pendingApproval: [],
      /**
       * List keeping the structure for displaying the client-grouped list
       * All timesheet data refers to pendingApproval's item
       * @type {Array<TimesheetsPrimaryGroupDisplayableItem>}
       */
      clientDisplayables: [],
      /**
       * List keeping the structure for displaying the candidate-grouped list
       * All timesheet data refers to pendingApproval's item
       * @type {Array<TimesheetsPrimaryGroupDisplayableItem>}
       */
      candidateDisplayables: [],
      /**
       * @type {Array<TimesheetsPendingApprovalViewModel>}
       */
      selectedTimesheets: [],
      totalApprovedCount: 0,
      totalPendingReviewCount: 0,
      totalPendingApprovalCount: 0,
      hasLoadedSuccessfully: false,
      showChangeDialog: false,
      changeTimeTimesheet: null,
      keyOfChangeTimeTimesheet: null,
      timesheetSubmissionIssues: [],
      primaryGrouping: TimesheetPrimaryGroupingType.client,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
    }),
    hasSelection() {
      return this.selectedCount > 0
    },
    hasTimesheetIssues() {
      return this.timesheetSubmissionIssues.length > 0
    },
    selectedCount() {
      return this.selectedTimesheets.length || 0
    },
    pendingApprovalCount() {
      return this.pendingApproval?.length || 0
    },
    hasBookingsPendingApproval() {
      return this.totalPendingApprovalCount > 0
    },
  },
  watch: {
    primaryGrouping(value) {
      this.selectedTimesheets = []
    },
    /**
     * Listen for changes to the selected clients and refresh the data based on
     * new selection
     */
    async getSelectedClients() {
      await this.refreshTimesheetsData()
    },
  },
  async created() {
    const result = await this.loadTimesheetsSummary()
    if (!result.isSuccess) return false

    this.initTimesheetDataStructure(result.data)
    this.hasLoadedSuccessfully = true
  },
  methods: {
    ...mapActions({
      loadTimesheetsSummary: 'timesheets/loadTimesheetsSummary',
      loadTimesheets: 'timesheets/loadTimesheets',
      loadTimesheetsFilteredByCandidate:
        'timesheets/loadTimesheetsFilteredByCandidate',
      submitTimesheetsForApproval: 'timesheets/submitTimesheetsForApproval',
    }),
    async refreshTimesheetsData() {
      this.hasLoadedSuccessfully = false
      this.clientDisplayables = []
      this.candidateDisplayables = []
      this.selectedTimesheets = []
      this.pendingApproval = []

      const result = await this.loadTimesheetsSummary()
      if (!result.isSuccess) return false

      this.initTimesheetDataStructure(result.data)
      this.hasLoadedSuccessfully = true
    },
    initTimesheetDataStructure(timesheetSummary) {
      for (const clientStat of timesheetSummary.clientTimesheetsSummary) {
        const clientDisplayableItem = new TimesheetsPrimaryGroupDisplayableItem(
          {
            id: clientStat.clientId,
            name: clientStat.clientName,
            approvedCount: clientStat.approvedCount,
            pendingReviewCount: clientStat.pendingReviewCount,
            pendingApprovalCount: clientStat.pendingApprovalCount,
          }
        )
        this.clientDisplayables.push(clientDisplayableItem)
      }
      for (const candidateStat of timesheetSummary.candidateTimesheetsSummary) {
        const candidateDisplayableItem = new TimesheetsPrimaryGroupDisplayableItem(
          {
            id: candidateStat.candidateId,
            name: candidateStat.candidateName,
            approvedCount: candidateStat.approvedCount,
            pendingReviewCount: candidateStat.pendingReviewCount,
            pendingApprovalCount: candidateStat.pendingApprovalCount,
          }
        )
        this.candidateDisplayables.push(candidateDisplayableItem)
      }

      this.totalApprovedCount = timesheetSummary.totalApprovedCount
      this.totalPendingReviewCount = timesheetSummary.totalPendingReviewCount
      this.totalPendingApprovalCount =
        timesheetSummary.totalPendingApprovalCount
    },
    async loadTimesheetByTypeAndId(type, id) {
      let result = []
      // load timesheet data depending on which primary group it is on
      if (type === TimesheetPrimaryGroupingType.client) {
        result = await this.loadTimesheets(id)
        if (!result.isSuccess) return false

        const displayableGroup = this.clientDisplayables.find(
          (client) => client.id === id
        )

        displayableGroup.pendingApprovalCount =
          result.data.pendingApprovalList.length
        displayableGroup.approvedCount = result.data.approvedCount
        displayableGroup.pendingReviewCount = result.data.pendingReviewCount
        // set this true to prevent loading this set again
        displayableGroup.isLoaded = true
      } else {
        result = await this.loadTimesheetsFilteredByCandidate(id)
        if (!result.isSuccess) return false

        const displayableGroup = this.candidateDisplayables.find(
          (candidate) => candidate.id === id
        )

        displayableGroup.pendingApprovalCount =
          result.data.pendingApprovalList.length
        displayableGroup.approvedCount = result.data.approvedCount
        displayableGroup.pendingReviewCount = result.data.pendingReviewCount
        // set this true to prevent loading this set again
        displayableGroup.isLoaded = true
      }

      for (const timesheet of result.data.pendingApprovalList) {
        // Check if this timesheet is already loaded before
        if (
          this.pendingApproval.find(
            (existingTimesheet) =>
              existingTimesheet.timesheet.bookingId === timesheet.bookingId
          )
        ) {
          continue
        }
        // update timesheet main list
        const newTimesheet = new TimesheetsPendingApprovalMainListItem({
          timesheet,
        })
        this.pendingApproval.push(newTimesheet)

        const weekRangeString = getWeekRangeStringFromTimesheet(
          timesheet,
          this.$i18n
        )

        // link new item to displayable lists
        this.linkNewTimesheetToClientDisplayable(newTimesheet, weekRangeString)
        this.linkNewTimesheetToCandidateDisplayable(
          newTimesheet,
          weekRangeString
        )
      }
    },
    linkNewTimesheetToClientDisplayable(newTimesheet, weekRangeString) {
      // get timesheet's client from displayable list
      let clientDisplayable = this.clientDisplayables.find(
        (client) => client.id === newTimesheet.timesheet.clientId
      )
      if (!clientDisplayable) {
        clientDisplayable = new TimesheetsPrimaryGroupDisplayableItem({
          id: newTimesheet.timesheet.clientId,
          name: newTimesheet.timesheet.clientName,
          isLoaded: false, // for this client set to be retrieved in full when this timesheet is loaded from candidate set
        })
        this.clientDisplayables.push(clientDisplayable)
      }
      // for new timesheet that emerged after initial-load/refresh, increment pending approval count for it to make sense until fully loaded
      // this is for "off-group set" loading
      if (
        clientDisplayable.pendingApprovalDisplayable.length ===
        clientDisplayable.pendingApprovalCount
      ) {
        clientDisplayable.pendingApprovalCount++
      }

      // link main list item to displayables
      const clientWeekGroup = clientDisplayable.pendingApprovalDisplayable.find(
        (group) => group.title === weekRangeString
      )
      if (!clientWeekGroup) {
        // if week group does not exist, create one and add its first item
        const newClientWeekGroup = new TimesheetsWeekGroupingListItem({
          title: weekRangeString,
          sortDate: newTimesheet.timesheet.startTimeLocal,
        })
        // for primary grouping as client, the leaf group is to be in candidate
        const newTimesheetsDict = {}
        newTimesheetsDict[newTimesheet.timesheet.candidateId] = [
          newTimesheet.timesheet,
        ]
        newClientWeekGroup.timesheets = newTimesheetsDict
        clientDisplayable.pendingApprovalDisplayable.push(newClientWeekGroup)

        // assign reference for later processes
        newTimesheet.refToClientDisplayable = clientDisplayable
        newTimesheet.refToClientWeekGroup = newClientWeekGroup
        newTimesheet.refToClientDictionaryValueArray =
          newTimesheetsDict[newTimesheet.timesheet.candidateId]
      } else {
        // for primary grouping as client, the leaf group is to be in candidate
        if (
          !Object.prototype.hasOwnProperty.call(
            clientWeekGroup.timesheets,
            newTimesheet.timesheet.candidateId
          )
        ) {
          clientWeekGroup.timesheets[newTimesheet.timesheet.candidateId] = []
        }
        clientWeekGroup.timesheets[newTimesheet.timesheet.candidateId].push(
          newTimesheet.timesheet
        )

        // assign reference for later processes
        newTimesheet.refToClientDisplayable = clientDisplayable
        newTimesheet.refToClientWeekGroup = clientWeekGroup
        newTimesheet.refToClientDictionaryValueArray =
          clientWeekGroup.timesheets[newTimesheet.timesheet.candidateId]
      }
    },
    linkNewTimesheetToCandidateDisplayable(newTimesheet, weekRangeString) {
      // get timesheet's candidate from displayable list
      let candidateDisplayable = this.candidateDisplayables.find(
        (candidate) => candidate.id === newTimesheet.timesheet.candidateId
      )
      if (!candidateDisplayable) {
        candidateDisplayable = new TimesheetsPrimaryGroupDisplayableItem({
          id: newTimesheet.timesheet.candidateId,
          name: newTimesheet.timesheet.candidateFullName,
          isLoaded: false, // for this candidate set to be retrieved in full when this timesheet is loaded from client set
        })
        this.candidateDisplayables.push(candidateDisplayable)
      }
      // for new timesheet that emerged after initial-load/refresh, increment pending approval count for it to make sense until fully loaded
      // this is for "off-group set" loading
      if (
        candidateDisplayable.pendingApprovalDisplayable.length ===
        candidateDisplayable.pendingApprovalCount
      ) {
        candidateDisplayable.pendingApprovalCount++
      }

      // link main list item to displayables
      const candidateWeekGroup = candidateDisplayable.pendingApprovalDisplayable.find(
        (group) => group.title === weekRangeString
      )
      if (!candidateWeekGroup) {
        // if week group does not exist, create one and add its first item
        const newCandidateWeekGroup = new TimesheetsWeekGroupingListItem({
          title: weekRangeString,
          sortDate: newTimesheet.timesheet.startTimeLocal,
        })
        // for primary grouping as candidate, the leaf group is to be in client
        const newTimesheetsDict = {}
        newTimesheetsDict[newTimesheet.timesheet.clientId] = [
          newTimesheet.timesheet,
        ]
        newCandidateWeekGroup.timesheets = newTimesheetsDict
        candidateDisplayable.pendingApprovalDisplayable.push(
          newCandidateWeekGroup
        )

        // assign reference for later processes
        newTimesheet.refToCandidateDisplayable = candidateDisplayable
        newTimesheet.refToCandidateWeekGroup = newCandidateWeekGroup
        newTimesheet.refToCandidateDictionaryValueArray =
          newTimesheetsDict[newTimesheet.timesheet.clientId]
      } else {
        // for primary grouping as candidate, the leaf group is to be in client
        if (
          !Object.prototype.hasOwnProperty.call(
            candidateWeekGroup.timesheets,
            newTimesheet.timesheet.clientId
          )
        ) {
          candidateWeekGroup.timesheets[newTimesheet.timesheet.clientId] = []
        }
        candidateWeekGroup.timesheets[newTimesheet.timesheet.clientId].push(
          newTimesheet.timesheet
        )

        // assign reference for later processes
        newTimesheet.refToCandidateDisplayable = candidateDisplayable
        newTimesheet.refToCandidateWeekGroup = candidateWeekGroup
        newTimesheet.refToCandidateDictionaryValueArray =
          candidateWeekGroup.timesheets[newTimesheet.timesheet.clientId]
      }
    },
    timesheetsUpdated(timesheets) {
      const flatPendingApprovalTimesheetBookingIds = this.pendingApproval.map(
        (booking) => booking.timesheet.bookingId
      )

      // Filter out timesheets that don't exist in the pending approval list.
      // Resolves a bug where selected bookings that are removed, still get
      // returned by v-data-table on select.
      const filteredTimesheets = timesheets.timesheets.filter((timesheet) =>
        flatPendingApprovalTimesheetBookingIds.includes(timesheet.bookingId)
      )

      // Since we receive only the selected timesheets from a group on a
      // selection change, it's easier to remove all the currently selected
      // timesheets from that group first, then readd them.
      this.selectedTimesheets = this.selectedTimesheets.filter(
        (selectedTimesheet) => selectedTimesheet.key !== timesheets.key
      )

      for (const timesheet of filteredTimesheets) {
        timesheet.key = timesheets.key
        this.selectedTimesheets.push(timesheet)
      }
    },
    changeTime(timesheetData) {
      this.changeTimeTimesheet = timesheetData.timesheet
      this.keyOfChangeTimeTimesheet = timesheetData.key
      this.showChangeDialog = true
    },
    handleDidNotHappenTimesheetSubmit(payload) {
      return this.processTimesheetSubmitResponseViewModel(
        payload.result.data,
        payload.timesheets,
        TimesheetOperationType.CANCEL
      )
    },
    handleModifyTimesheetSubmit(payload) {
      return this.processTimesheetSubmitResponseViewModel(
        payload.result.data,
        payload.timesheets,
        TimesheetOperationType.MODIFY
      )
    },
    removeProcessedTimesheetsFromSelection(processedTimesheets) {
      const processedBookingIds = processedTimesheets.map(
        (timesheet) => timesheet.bookingId
      )
      this.selectedTimesheets = this.selectedTimesheets.filter(
        (selectedTimesheet) =>
          !processedBookingIds.includes(selectedTimesheet.bookingId)
      )
    },
    /**
     * Updates counters and deselects processed timesheets
     * @param {TimesheetSubmitResponseViewModel} responseObj
     * @param {Array} flatSelectedTimesheets
     */
    processTimesheetSubmitResponseViewModel(
      responseObj,
      flatSelectedTimesheets,
      operationType
    ) {
      const responseVM = new TimesheetSubmitResponseViewModel(responseObj)

      this.totalPendingApprovalCount -=
        responseVM.approvedForInvoiceCount + responseVM.pendingReviewCount
      this.totalApprovedCount += responseVM.approvedForInvoiceCount
      this.totalPendingReviewCount += responseVM.pendingReviewCount

      // Clear selected timesheets from the UI
      for (const processedTimesheet of flatSelectedTimesheets) {
        const mainListIndexOfTimesheet = this.pendingApproval.findIndex(
          (timesheet) =>
            timesheet.timesheet.bookingId === processedTimesheet.bookingId
        )
        if (!mainListIndexOfTimesheet < 0) continue

        const mainListItemWithRefToDisplayable = this.pendingApproval[
          mainListIndexOfTimesheet
        ]

        const matchingItemFromIssues = this.timesheetSubmissionIssues.find(
          (issue) => issue.bookingId === processedTimesheet.bookingId
        )
        const singleCountOfThisTimesheet = this.determineCountFromSubmissionResult(
          mainListItemWithRefToDisplayable.timesheet,
          operationType,
          matchingItemFromIssues
        )

        this.processTimesheetSubmitResponseForClientDisplayable(
          mainListItemWithRefToDisplayable,
          singleCountOfThisTimesheet
        )
        this.processTimesheetSubmitResponseForCandidateDisplayable(
          mainListItemWithRefToDisplayable,
          singleCountOfThisTimesheet
        )

        // get rid of the timesheet from main list
        this.pendingApproval.splice(mainListIndexOfTimesheet, 1)
      }

      this.timesheetSubmissionIssues = responseVM.errors

      this.removeProcessedTimesheetsFromSelection(flatSelectedTimesheets)
    },
    processTimesheetSubmitResponseForClientDisplayable(
      mainListItemWithRefToDisplayable,
      count
    ) {
      mainListItemWithRefToDisplayable.refToClientDisplayable
        .pendingApprovalCount--
      mainListItemWithRefToDisplayable.refToClientDisplayable.approvedCount +=
        count.approvedCount
      mainListItemWithRefToDisplayable.refToClientDisplayable.pendingReviewCount +=
        count.pendingReviewCount
      // delete timesheet in displayable
      const indexOfTimesheetToBeRemoved = mainListItemWithRefToDisplayable.refToClientDictionaryValueArray.findIndex(
        (x) =>
          x.bookingId === mainListItemWithRefToDisplayable.timesheet.bookingId
      )
      if (indexOfTimesheetToBeRemoved < 0) return

      mainListItemWithRefToDisplayable.refToClientDictionaryValueArray.splice(
        indexOfTimesheetToBeRemoved,
        1
      )

      // If there are no more rows in the table, remove the leaf-group table
      if (
        mainListItemWithRefToDisplayable.refToClientDictionaryValueArray
          .length === 0
      ) {
        this.$delete(
          mainListItemWithRefToDisplayable.refToClientWeekGroup.timesheets,
          mainListItemWithRefToDisplayable.timesheet.candidateId
        )
      }

      // If there are no more timesheets in a week, remove week grouping
      if (
        Object.keys(
          mainListItemWithRefToDisplayable.refToClientWeekGroup.timesheets
        ).length === 0
      ) {
        mainListItemWithRefToDisplayable.refToClientDisplayable.pendingApprovalDisplayable = mainListItemWithRefToDisplayable.refToClientDisplayable.pendingApprovalDisplayable.filter(
          (groupItem) =>
            groupItem.title !==
            mainListItemWithRefToDisplayable.refToClientWeekGroup.title
        )
      }
    },
    processTimesheetSubmitResponseForCandidateDisplayable(
      mainListItemWithRefToDisplayable,
      count
    ) {
      mainListItemWithRefToDisplayable.refToCandidateDisplayable
        .pendingApprovalCount--
      mainListItemWithRefToDisplayable.refToCandidateDisplayable.approvedCount +=
        count.approvedCount
      mainListItemWithRefToDisplayable.refToCandidateDisplayable.pendingReviewCount +=
        count.pendingReviewCount
      // delete timesheet in displayable
      const indexOfTimesheetToBeRemoved = mainListItemWithRefToDisplayable.refToCandidateDictionaryValueArray.findIndex(
        (x) =>
          x.bookingId === mainListItemWithRefToDisplayable.timesheet.bookingId
      )
      if (indexOfTimesheetToBeRemoved < 0) return

      mainListItemWithRefToDisplayable.refToCandidateDictionaryValueArray.splice(
        indexOfTimesheetToBeRemoved,
        1
      )

      // If there are no more rows in the table, remove the leaf-group table
      if (
        mainListItemWithRefToDisplayable.refToCandidateDictionaryValueArray
          .length === 0
      ) {
        this.$delete(
          mainListItemWithRefToDisplayable.refToCandidateWeekGroup.timesheets,
          mainListItemWithRefToDisplayable.timesheet.clientId
        )
      }

      // If there are no more timesheets in a week, remove week grouping
      if (
        Object.keys(
          mainListItemWithRefToDisplayable.refToCandidateWeekGroup.timesheets
        ).length === 0
      ) {
        mainListItemWithRefToDisplayable.refToCandidateDisplayable.pendingApprovalDisplayable = mainListItemWithRefToDisplayable.refToCandidateDisplayable.pendingApprovalDisplayable.filter(
          (groupItem) =>
            groupItem.title !==
            mainListItemWithRefToDisplayable.refToCandidateWeekGroup.title
        )
      }
    },
    async submitTimesheets() {
      return await this.handleTimesheetSubmit(this.selectedTimesheets)
    },
    async handleTimesheetSubmit(timesheets) {
      const payload = this.mapTimesheetListToPayload(timesheets)

      const result = await this.submitTimesheetsForApproval(payload)

      if (!result?.isSuccess) return

      return this.processTimesheetSubmitResponseViewModel(
        result.data,
        timesheets,
        TimesheetOperationType.ACCEPT
      )
    },
  },
}
</script>

<template>
  <Layout>
    <div class="d-flex align-center justify-space-between">
      <BasePageTitle
        :title="$t('timesheets.timesheetPageTitle')"
        :subtitle="$t('timesheets.timesheetPageSubtitle')"
      />
      <TimesheetPrimaryGroupingToggle v-model="primaryGrouping" />
    </div>
    <TimesheetExpPanelsSkeletonLoader v-if="!hasLoadedSuccessfully" />
    <div v-else>
      <TimesheetSubmitIssuesAlert
        v-show="hasTimesheetIssues"
        :issues="timesheetSubmissionIssues"
      />
      <div v-if="hasBookingsPendingApproval">
        <TimesheetPrimaryGroupPanel
          :primary-grouping="primaryGrouping"
          :client-displayables="clientDisplayables"
          :candidate-displayables="candidateDisplayables"
          :load-timesheet-handler="loadTimesheetByTypeAndId"
          @timesheetsUpdated="timesheetsUpdated"
          @changeTime="changeTime"
          @cancelBookingSubmitted="handleDidNotHappenTimesheetSubmit"
        />
      </div>
      <EmptyStateBlock v-else border>
        <template v-slot:icon>
          <v-icon color="primary" class="mb-5" size="62">
            mdi-format-list-bulleted
          </v-icon>
        </template>

        <template v-slot:title>
          <h3 :class="['mb-2 font-weight-bold']">{{
            $t('timesheets.emptyState.title')
          }}</h3>
        </template>

        <template v-slot:description>
          <p :class="['text-subtitle-2']">{{
            $t('timesheets.emptyState.description')
          }}</p>
        </template>
      </EmptyStateBlock>
    </div>

    <TimesheetSelectionActionBar
      :selected-timesheets="selectedTimesheets"
      :approved-for-invoice-count="totalApprovedCount"
      :pending-review-count="totalPendingReviewCount"
      @submitTimesheets="submitTimesheets"
    />

    <TimesheetChangeTimeCard
      v-model="showChangeDialog"
      :timesheet="changeTimeTimesheet"
      @closeModal="showChangeDialog = !showChangeDialog"
      @submitted="handleModifyTimesheetSubmit"
    />
  </Layout>
</template>
